import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button, Modal, Spinner } from "react-bootstrap";
import api from "../../utils/api";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import he from "he";
import parse from "html-react-parser";
import {
  FiSearch,
  FiFilter,
  FiTrash2,
  FiEdit,
  FiEye,
  FiEyeOff,
  FiBook,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiHelpCircle,
  FiAward,
  FiAlertTriangle,
  FiArrowLeft,
  FiPlus,
  FiDownload,
  FiBookOpen,
  FiClipboard,
  FiTrendingUp,
  FiCheck,
  FiX
} from 'react-icons/fi';

const QuestionDetails = () => {
  const currentPageRef = useRef(1);
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [examTypeFilter, setExamTypeFilter] = useState("");
  const [show, setShow] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedQuestionType, setSelectedQuestionType] = useState("");
  const [selectedExamType, setSelectedExamType] = useState("");
  const [buttonText, setButtonText] = useState("Yes, Delete");
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [levels, setLevels] = useState([]);
  const [questionType, setQuestionType] = useState([]);
  const [examType, setExamType] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [userPermissions, setUserPermissions] = useState({});
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const { id } = useParams();
  const [dots, setDots] = useState("");
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const loadingMessages = ["Loading...", "Rendering LaTeX...", "Converting..."];
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState("");
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsTab, setStatsTab] = useState("correct");
  const [statsLoading, setStatsLoading] = useState(false);
  const [showAllLoading, setShowAllLoading] = useState(false);
  const [showAllDone, setShowAllDone] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setLoadingMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length);
    }, 500);
    return () => clearInterval(messageInterval);
  }, []);

  useEffect(() => {
    auth();
    fetchData();
    fetchModules();
    fetchExamType();
    fetchLevel();
    fetchQuestionType();
  }, []);

  async function auth() {
    setLoading(true);
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const role = localStorage.getItem("role");
      const token = localStorage.getItem("authToken");
      const programType = "getPagePermission";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("role", role);
      fd.append("programType", programType);
      fd.append("limit", itemsPerPage);
      fd.append("pageNo", 9);

      const response = await api.post("user/userCurl", fd);
      if (response.data && response.data.data.length > 0) {
        setUserPermissions(response.data.data[0]);
      }
    } catch (error) {
      setUserPermissions({});
    } finally {
      setLoading(false);
    }
  }

  async function fetchData(
    page = 1,
    limit = 50,
    subjectId = null,
    chapterId = null,
    topicId = null,
    examType = null,
    level = null,
    questionType = null,
    search = null
  ) {
    const startApiTime = performance.now();
    const loggedInUser = localStorage.getItem("loggedInUser");
    const token = localStorage.getItem("authToken");
    const programType = "getQuestionDetailsMain";

    try {
      if (loading) return;
      setLoading(true);
      setNotFound(false);

      const postData = async (url, data) => {
        const fd = new FormData();
        Object.entries(data).forEach(([key, value]) => value && fd.append(key, value));
        Object.entries(data).forEach(([key, value]) => {
          if (value) fd.append(key, value);
        });

        const response = await api.post(url, fd);
        console.log("API Response:", response.data);
        return response.data || {};
      };

      const filterData = {
        programType,
        loggedInUser,
        token,
        page,
        limit,
        paperId: id,
        search: search || null,
        subject: subjectId,
        chapter: chapterId,
        topic: topicId,
        examType,
        level,
        questionType,
      };

      console.log("Fetching questions with filters:", filterData);

      const questionData = await postData("question/questionCurl", filterData);
      const endApiTime = performance.now();
      // console.log(`⏱ API fetch time: ${(endApiTime - startApiTime).toFixed(2)} ms`);

      const questions = Array.isArray(questionData.questions) ? questionData.questions : [];

      if (page === 1) {
        setInitialLoading(false);
        setLoading(false);
        setAllQuestions(questionData.questions);
      } else {
        setAllQuestions((prev) => [...prev, ...questions]);
      }

      setTotalItems(questionData.totalItems);
      setCurrentPage(page);
    } catch (error) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }

  async function fetchAnswers(questionId) {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getOptionsAndAnsForQuestion";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("questionId", questionId);

      const response = await api.post("question/questionCurl", fd);
      return response.data;
    } catch (error) {
      console.error("Error fetching answers:", error);
      return null;
    }
  }

  async function fetchAnswersMultiple(questionIds = []) {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getOptionsAndAnsForQuestionInArray";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);

      questionIds.forEach((id) => {
        fd.append("questionId[]", id);
      });

      const response = await api.post("question/questionCurl", fd);
      return response.data;
    } catch (error) {
      console.error("Error fetching multiple answers:", error);
      return null;
    }
  }

  const handleShowAll = async () => {
    if (selectedQuestions.length === 0) return;
    setShowAllLoading(true);
    try {
      const res = await fetchAnswersMultiple(selectedQuestions);
      const answerObj = res?.data;

      if (!answerObj || typeof answerObj !== "object") {
        toast.error("Failed to load answers");
        return;
      }

      const updatedQuestions = allQuestions.map((q) => {
        if (!selectedQuestions.includes(q.id)) return q;

        const ans = answerObj[q.id];
        if (!ans) return q;

        return {
          ...q,
          options: Array.isArray(ans.options) && ans.options.length ? ans.options : q.options,
          answer: ans.answer ?? q.answer ?? "",
          explanation: ans.explanation ?? q.explanation ?? "",
          optionId: ans.optionId ?? q.optionId ?? "",
          showAnswer: true,
        };
      });

      setAllQuestions(updatedQuestions);
      setShowAllDone(true);
      setSelectedQuestions([]);
    } catch (err) {
      toast.error("Something went wrong while loading answers");
    } finally {
      setShowAllLoading(false);
    }
  };

  async function fetchStats(questionId) {
    try {
      setStatsLoading(true);
      setStatsTab("correct");
      const loggedInUser = localStorage.getItem("loggedInUser");
      const role = localStorage.getItem("role");
      const token = localStorage.getItem("authToken");

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("role", role);
      fd.append("programType", "getStudentsByQuestionAcrossPapers");
      fd.append("questionId", questionId);

      const response = await api.post("picQuestion/picQuestionCurl", fd);
      setStatsData(response.data);
      setShowStatsModal(true);
    } catch (error) {
      console.error("❌ Error fetching statistics:", error);
    } finally {
      setStatsLoading(false);
    }
  }

  const handleLoadMore = () => {
    if (loading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchData(
      nextPage,
      itemsPerPage,
      selectedSubject,
      selectedChapter,
      selectedTopic,
      examTypeFilter,
      selectedLevel,
      selectedQuestionType,
      searchQuery
    );
  };

  useEffect(() => {
    if (searchQuery === "") {
      setIsSearchActive(false);
      fetchData();
    }
  }, [searchQuery]);

 const handleGoClick = () => {
  fetchData(
    1,
    itemsPerPage,
    selectedSubject,
    selectedChapter,
    selectedTopic,
    selectedExamType,   // 👈 this is ID
    selectedLevel,
    selectedQuestionType,
    searchQuery?.trim() || null
  );
};

  useEffect(() => {
    currentPageRef.current = 1;
    fetchData(
      1,
      itemsPerPage,
      selectedSubject,
      selectedChapter,
      selectedTopic,
      selectedExamType,
      selectedLevel,
      selectedQuestionType
    );
  }, []);

  const renderLatexText = (text, layout = "single", fontSize = "16px") => {
    if (!text) return null;

    if (typeof text !== "string") {
      if (text && typeof text.toString === "function") {
        text = text.toString();
      } else {
        return null;
      }
    }

    let decodedText = he.decode(text);
    decodedText = decodedText.replace(/<br\s*\/?>/gi, "\n");

    const latexAndImageRegex =
      /\\\((.*?)\\\)|\\\[(.*?)\\\]|<img[^>]*\bsrc=(["'])(.*?)\3[^>]*>|\\begin{align}([\s\S]*?)\\end{align}/g;

    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = latexAndImageRegex.exec(decodedText)) !== null) {
      const [fullMatch, inlineLatex, blockLatex, , imageSrc, alignContent] = match;
      const matchIndex = match.index;

      if (matchIndex > lastIndex) {
        const plainText = decodedText.slice(lastIndex, matchIndex);
        plainText.split("\n").forEach((line, i, arr) => {
          if (line) parts.push(<span key={`html-${key++}`}>{parse(line)}</span>);
          if (i < arr.length - 1) parts.push(<br key={`br-${key++}`} />);
        });
      }

      if (imageSrc) {
        parts.push(
          <img
            key={`img-${key++}`}
            src={imageSrc}
            alt="Image"
            style={{
              maxWidth: `calc(${fontSize} * 18)`,
              display: "inline",
              verticalAlign: "middle",
              marginLeft: "4px",
              marginRight: "4px",
            }}
          />
        );
      } else if (alignContent) {
        parts.push(
          <BlockMath key={`align-${key++}`}>
            {`\\begin{align}${alignContent}\\end{align}`}
          </BlockMath>
        );
      } else if (inlineLatex || blockLatex) {
        parts.push(
          <InlineMath key={`inline-${key++}`}>
            {inlineLatex || blockLatex}
          </InlineMath>
        );
      }

      lastIndex = matchIndex + fullMatch.length;
    }

    if (lastIndex < decodedText.length) {
      const remaining = decodedText.slice(lastIndex);
      remaining.split("\n").forEach((line, i, arr) => {
        if (line) parts.push(<span className="normal-inline" key={`html-${key++}`}>{parse(line)}</span>);
        if (i < arr.length - 1) parts.push(<br key={`br-${key++}`} />);
      });
    }

    return <span style={{ whiteSpace: "normal", display: "inline" }}>{parts}</span>;
  };

  useEffect(() => {
    if (selectedModule) {
      fetchSubjects(selectedModule);
    } else {
      setSubjects([]);
    }
    setSelectedSubject("");
    setSelectedChapter("");
    setSelectedTopic("");
    setChapters([]);
    setTopics([]);
  }, [selectedModule]);

  async function fetchModules() {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getAllDetails";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);

      const modulesResponse = await api.post("module/moduleCurl", fd);
      if (Array.isArray(modulesResponse.data.getResponse)) {
        setModules(modulesResponse.data.getResponse);
      } else {
        setModules([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }

  async function fetchSubjects(moduleId) {
    if (!moduleId) {
      setSubjects([]);
      return;
    }

    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const role = localStorage.getItem("role");
      const token = localStorage.getItem("authToken");

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("role", role);
      fd.append("programType", "getSubjectByModule");
      fd.append("module", moduleId);

      const subjectResponse = await api.post("subject/subjectCurl", fd);
      if (Array.isArray(subjectResponse.data.data)) {
        setSubjects(subjectResponse.data.data);
      } else {
        setSubjects([]);
      }
    } catch (error) {
      setSubjects([]);
    }
  }

  async function fetchChapters(subjectId) {
    if (!subjectId) {
      setChapters([]);
      return;
    }

    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getChapterOnChangeMultiple";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("subjectId[]", subjectId);

      const chapterResponse = await api.post("onchange/onchangeCurl", fd);
      if (Array.isArray(chapterResponse.data.data)) {
        setChapters(chapterResponse.data.data);
      } else {
        setChapters([]);
      }
    } catch (error) {
      setChapters([]);
    }
  }

  async function fetchTopics(chapterId) {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getTopicOnChangeMultiple";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("paperId", id);
      fd.append("chapterId[]", chapterId);

      const response = await api.post("onchange/onchangeCurl", fd);
      if (Array.isArray(response.data.data)) {
        setTopics(response.data.data);
      } else {
        setTopics([]);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      setTopics([]);
    }
  }

  useEffect(() => {
    if (selectedChapter) {
      fetchTopics(selectedChapter);
    } else {
      setSelectedTopic("");
    }
  }, [selectedChapter]);

  async function fetchExamType() {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", "getExamTypes");

      const response = await api.post("question/examType", fd);

      if (response.data?.status) {
        setExamType(response.data.examTypes);
      }
    } catch (error) {
      console.error("Error fetching exam types:", error);
    }
  }
  async function fetchLevel() {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getLevelDetails";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);

      const response = await api.post("level/levelCurl", fd);
      if (Array.isArray(response.data.getLevelDetails)) {
        setLevels(response.data.getLevelDetails);
      } else {
        setLevels([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLevels([]);
    }
  }

  async function fetchQuestionType() {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getQuestionTypeDetails";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);

      const response = await api.post("questionType/questionTypeCurl", fd);
      if (Array.isArray(response.data.getQuestionTypeDetails)) {
        setQuestionType(response.data.getQuestionTypeDetails);
      } else {
        setQuestionType([]);
      }
    } catch (error) {
      setQuestionType([]);
    }
  }

  const handleShowAdd = (questionId) => {
    setSelectedQuestionId(questionId);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setSelectedQuestionId(null);
  };

  const handleDeleteQuestion = async () => {
    if (!selectedQuestionId) return;

    try {
      setLoading(true);
      setButtonText("Deleting...");

      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "deleteQuestion";

      const fd = new FormData();
      fd.append("programType", programType);
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("id[]", selectedQuestionId);

      const response = await api.post("question/questionCurl", fd);
      if (response.data && response.data.message) {
        fetchData();
        toast.success(response.data.message);
      } else {
        toast.error(response?.data?.data?.[0]);
      }

      setShow(false);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.data?.[0]);
    } finally {
      setLoading(false);
      setButtonText("Yes, Delete");
    }
  };

  const handleMarkPractice = async () => {
    if (selectedQuestions.length === 0) {
      toast.warning("Please select at least one question to mark as practice.");
      return;
    }

    try {
      setLoading(true);
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");

      const fd = new FormData();
      fd.append("programType", "markQuestionAsPractice");
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);

      selectedQuestions.forEach((id) => {
        fd.append("id[]", id);
      });

      const response = await api.post("question/questionCurl", fd);
      if (response.data?.status) {
        toast.success(response.data.message || "Marked as practice successfully");
        setSelectedQuestions([]);
        fetchData();
      } else {
        toast.error(response.data?.data?.[0] || "Failed to mark practice");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.data?.[0] ||
        error?.response?.data?.message ||
        "Error marking questions as practice"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestionBulk = async () => {
    if (selectedQuestions.length === 0) {
      toast.warning("Please select at least one question to delete.");
      return;
    }

    try {
      setLoading(true);
      setButtonText("Deleting...");

      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "deleteQuestion";

      const fd = new FormData();
      fd.append("programType", programType);
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);

      selectedQuestions.forEach((id) => {
        fd.append("id[]", id);
      });

      const response = await api.post("question/questionCurl", fd);
      if (response.data.status) {
        toast.success(response?.data?.data || "Questions deleted successfully.");
        setSelectedQuestions([]);
        fetchData();
      } else {
        const message = response?.data?.data?.[0] || "Failed to delete questions.";
        toast.error(message);
      }
    } catch (error) {
      const message =
        error?.response?.data?.data?.[0] ||
        error?.response?.data?.message ||
        "Error deleting questions.";
      toast.error(message);
      console.error("Error deleting questions:", error);
    } finally {
      setLoading(false);
      setButtonText("Yes, Delete");
    }
  };

  const handleSelectAll = () => {
    if (selectedQuestions.length === allQuestions.length) {
      setSelectedQuestions([]);
    } else {
      const allIds = allQuestions.map((q) => q.id);
      setSelectedQuestions(allIds);
    }
  };

  const handleCheckboxChange = (id) => {
    if (selectedQuestions.includes(id)) {
      setSelectedQuestions(selectedQuestions.filter((qid) => qid !== id));
    } else {
      setSelectedQuestions([...selectedQuestions, id]);
    }
  };

  const shouldUseSingleColumn = (options = []) => {
    return options.some((opt) => {
      const text =
        typeof opt === "string"
          ? opt
          : opt?.option || opt?.value || opt?.text || "";
      return text.length > 50;
    });
  };


  return (
    <div className="questions-container">
      <ToastContainer />


      {/* Header */}
      {/* <div className="questions-header">
        <div className="header-content">
          <h1>📚 Question Bank</h1>
          <p>Manage and analyze questions with detailed insights</p>
        </div>
      </div> */}

      {/* Main Content Grid */}
      <div className="questions-content-grid">
        {/* Filters Sidebar */}
        <div className={`filters-sidebar ${showFilters ? "show" : ""}`}>
          <div className="filters-card">
            <div className="filters-header">
              <h3><FiFilter /> Filters</h3>
              <button className="close-filters" onClick={() => setShowFilters(false)}>×</button>
            </div>
            <div className="filters-content">
              <div className="filter-group">
                <label>Class</label>
                <select
                  className="filter-select"
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                >
                  <option value="">Select Class</option>
                  {modules.map((mod) => (
                    <option key={mod.id} value={mod.id}>{mod.moduleName}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Subject</label>
                <select
                  className="filter-select"
                  value={selectedSubject}
                  onChange={async (e) => {
                    const subjectId = e.target.value;
                    setSelectedSubject(subjectId);
                    setSelectedChapter("");
                    setSelectedTopic("");
                    setChapters([]);
                    setTopics([]);
                    if (subjectId) {
                      await fetchChapters(subjectId);
                    }
                  }}
                >
                  <option value="">Select Subject</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.subjectName} ({subject.subjectCount})
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Chapter</label>
                <select
                  className="filter-select"
                  value={selectedChapter}
                  onChange={async (e) => {
                    const chapterId = e.target.value;
                    setSelectedChapter(chapterId);
                    setSelectedTopic("");
                    setTopics([]);
                    if (chapterId) {
                      await fetchTopics(chapterId);
                    }
                  }}
                >
                  <option value="">Select Chapter</option>
                  {chapters.map((chapter) => (
                    <option key={chapter.id} value={chapter.id}>
                      {chapter.chapterName} ({chapter.chapterCount})
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Topic</label>
                <select
                  className="filter-select"
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                >
                  <option value="">Select Topic</option>
                  {topics.map((topic) => (
                    <option key={topic.id} value={topic.id}>
                      {topic.topicName} ({topic.topicCount})
                    </option>
                  ))}
                </select>
              </div>

          
                <div className="filter-group">
                  <label>Exam Type</label>
                  <select
                    className="filter-select"
                    value={selectedExamType}
                    onChange={(e) => setSelectedExamType(e.target.value)}
                  >
                    <option value="">Select Exam Type</option>

                    {examType.map((exam) => (
                      <option key={exam.id} value={exam.id}>
                        {exam.examType}
                      </option>
                    ))}
                  </select>
                </div>
            

              <div className="filter-group">
                <label>Level</label>
                <select
                  className="filter-select"
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                >
                  <option value="">Select Level</option>
                  {levels.map((level) => (
                    <option key={level.id} value={level.id}>{level.levelName}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Question Type</label>
                <select
                  className="filter-select"
                  value={selectedQuestionType}
                  onChange={(e) => setSelectedQuestionType(e.target.value)}
                >
                  <option value="">Select Type</option>
                  {questionType.map((questionType) => (
                    <option key={questionType.id} value={questionType.id}>
                      {questionType.questionType}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-buttons">
                <button className="filter-btn primary" onClick={handleGoClick}>
                  Apply Filters
                </button>
                <button className="filter-btn secondary" onClick={() => {
                  setSelectedModule("");
                  setSelectedSubject("");
                  setSelectedChapter("");
                  setSelectedTopic("");
                 setSelectedExamType("");
                  setSelectedLevel("");
                  setSelectedQuestionType("");
                  fetchData(1, itemsPerPage);
                }}>
                  Reset All
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="questions-main">
          {/* Toolbar */}
          {/* Toolbar */}
          <div className="questions-toolbar">
            {/* Mobile filter toggle */}
            <button className="filter-toggle" onClick={() => setShowFilters(!showFilters)}>
              <FiFilter /> Filters
            </button>

            {/* All controls in one row */}
            <div className="toolbar-controls">
              {/* Search */}
              <div className="search-container">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by Question ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleGoClick()}
                />
              </div>

              {/* Action buttons */}
              <div className="toolbar-actions">
                {/* Show All button */}
                {selectedQuestions.length > 0 && !showAllDone && (
                  <button
                    className="action-button primary"
                    onClick={handleShowAll}
                    disabled={showAllLoading}
                  >
                    {showAllLoading ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Loading...
                      </>
                    ) : (
                      `Show All (${selectedQuestions.length})`
                    )}
                  </button>
                )}

                {/* Add/Import buttons */}
                {userPermissions.is_add === "1" && (
                  <>
                    <button className="action-button success" onClick={() => navigate("/add-question")}>
                      <FiPlus /> Add
                    </button>
                    <button className="action-button warning" onClick={() => navigate("/import-question")}>
                      <FiDownload /> Import
                    </button>
                  </>
                )}

                {/* Select All */}
                <button className="action-button select-all" onClick={handleSelectAll}>
                  {selectedQuestions.length === allQuestions.length ? "Deselect All" : "Select All"}
                </button>

                {/* Bulk actions */}
                {selectedQuestions.length > 0 && (
                  <>
                    <button className="action-button danger" onClick={handleDeleteQuestionBulk}>
                      <FiTrash2 /> Delete
                    </button>
                    <button className="action-button practice" onClick={handleMarkPractice}>
                      <FiBookOpen /> Practice
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Questions List */}
          <div className="questions-list">
            {initialLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>{loadingMessages[loadingMessageIndex]}</p>
              </div>
            ) : notFound ? (
              <div className="empty-state">
                <FiBook size={48} />
                <h3>No Questions Found</h3>
                <p>Try adjusting your filters or search criteria</p>
              </div>
            ) : (
              allQuestions.map((question, index) => (
                <div key={index} className="question-card">
                  <div className="question-header">
                    <div className="question-info">
                      <div className="question-number">Q{index + 1}</div>
                      <div className="question-id">ID: {question.id}</div>

                      <div className="question-tags">
                        <span className="tag level">{question.levelName}</span>
                        <span className="tag exam-type" dangerouslySetInnerHTML={{ __html: question.examType }} />
                        {Number(question.isPractice) === 1 && (
                          <span className="tag practice">📝 Practice</span>
                        )}
                      </div>

                    </div>
                    <div className="question-actions">
                      {userPermissions.is_edit === "1" && (
                        <button className="action-icon edit" onClick={() => navigate(`/add-question/${question.id}`)}>
                          <FiEdit size={14} />
                        </button>
                      )}
                      <button className="action-icon stats" onClick={() => fetchStats(question.id)}>
                        <FiTrendingUp size={14} />
                      </button>
                      {userPermissions.is_delete === "1" && (
                        <input
                          type="checkbox"
                          className="question-checkbox"
                          checked={selectedQuestions.includes(question.id)}
                          onChange={() => handleCheckboxChange(question.id)}
                        />
                      )}
                    </div>
                  </div>


                  <div className="question-content">
                    {renderLatexText(question.question)}
                  </div>

                  {question.options?.length > 0 && (
                    <div className="options-section">
                      <div className="options-header">Options</div>

                      <div
                        className="options-grid"
                        style={{
                          gridTemplateColumns: shouldUseSingleColumn(question.options)
                            ? "1fr"
                            : "1fr 1fr",
                        }}
                      >
                        {question.options.map((option, i) => {
                          let optionText = "";

                          if (typeof option === "string") {
                            optionText = option;
                          } else if (option && typeof option === "object") {
                            optionText =
                              option.option || option.value || option.text || "";
                          } else {
                            optionText = String(option || "");
                          }

                          return (
                            <div key={i} className="option-item">
                              <div className="option-letter">{i + 1}</div>
                              <div className="option-text">
                                {renderLatexText(optionText)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}


                  <div className="question-footer">
                    <button
                      className="toggle-answer"
                      onClick={async () => {
                        const updated = [...allQuestions];
                        if (!updated[index].showAnswer) {
                          const data = await fetchAnswers(question.id);
                          if (data) {
                            updated[index] = {
                              ...updated[index],
                              options: data.options || [],
                              answer: data.answer || "",
                              explanation: data.explanation || "",
                              optionId: data.optionId || "",
                            };
                          }
                        }
                        updated[index].showAnswer = !updated[index].showAnswer;
                        setAllQuestions(updated);
                      }}
                    >
                      {question.showAnswer ? (
                        <>
                          <FiEyeOff /> Hide Details
                        </>
                      ) : (
                        <>
                          <FiEye /> Show Details
                        </>
                      )}
                    </button>
                  </div>

                  {question.showAnswer && (
                    <div className="answer-section">
                      <div className="answer-divider"></div>
                      <div className="answer-content">
                        <div className="answer-field">
                          <strong>Explanation:</strong>
                          <div className="answer-text">{renderLatexText(question.explanation)}</div>
                        </div>

                        <div className="answer-field">
                          <strong>Answer:</strong>
                          <div className="answer-text">
                            {renderLatexText(question.answer)}

                            {question.options && question.options.length > 0 ? (
                              <span className="option-id">
                                (Option {question.optionId})
                              </span>
                            ) : <span className="option-id">
                              {question.optionId}
                            </span>}
                          </div>
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              ))
            )}

            {!initialLoading && hasMore && !loading && !searchQuery && (
              <div className="load-more">
                <button className="load-more-button" onClick={handleLoadMore} disabled={loading}>
                  Load More Questions
                </button>
              </div>
            )}

            {loading && !initialLoading && (
              <div className="loading-more">
                <Spinner animation="border" size="sm" />
                <span>Loading more questions...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistics Modal */}
      {showStatsModal && (
        <div className="modal-overlay" onClick={() => setShowStatsModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 Question Statistics</h3>
              <button className="modal-close" onClick={() => setShowStatsModal(false)}>×</button>
            </div>

            <div className="modal-tabs">
              {["correct", "wrong", "unanswered"].map(tab => (
                <button
                  key={tab}
                  className={`modal-tab ${statsTab === tab ? "active" : ""}`}
                  onClick={() => setStatsTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)} ({statsData[tab]?.count || 0})
                </button>
              ))}
            </div>

            <div className="modal-content">
              {statsLoading ? (
                <div className="loading-modal">
                  <Spinner animation="border" />
                  <p>Loading statistics...</p>
                </div>
              ) : statsData[statsTab]?.students?.length === 0 ? (
                <div className="empty-modal">
                  <FiUsers size={32} />
                  <p>No students found in this category</p>
                </div>
              ) : (
                <div className="students-list">
                  {statsData[statsTab]?.students?.map((s, i) => (
                    <div key={i} className="student-item">
                      <div className="student-info">
                        <div className="student-name">{s.name}</div>
                        <div className="student-roll">Roll: {s.rollNumber || "-"}</div>
                      </div>
                      <button
                        className="view-student-button"
                        onClick={() => navigate(`/student-overall/${s.studentId}`)}
                      >
                        View Profile
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this question?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteQuestion} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" /> {buttonText}
              </>
            ) : (
              buttonText
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>
        {`
          .questions-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%);
            padding: 1.5rem;
          }

         

         

          /* Header */
          .questions-header {
           
            margin: 0 auto 2rem;
            background: white;
            border-radius: 1rem;
            padding: 1.5rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            border: 1px solid #e2e8f0;
          }

          .header-content h1 {
            font-size: 1.75rem;
            font-weight: 700;
            color: #1f2937;
            margin: 0 0 0.5rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }

          .header-content p {
            font-size: 0.875rem;
            color: #6b7280;
            margin: 0;
          }

          /* Content Grid */
          .questions-content-grid {
          
            margin: 0 auto;
            display: grid;
            grid-template-columns: 280px 1fr;
            gap: 1.5rem;
          }

          /* Filters Sidebar */
          .filters-sidebar {
            position: sticky;
            top: 1.5rem;
            height: fit-content;
          }

          .filters-card {
            background: white;
            border-radius: 1rem;
            border: 1px solid #e2e8f0;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            overflow: hidden;
          }

          .filters-header {
            padding: 1.25rem;
            border-bottom: 1px solid #f3f4f6;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .filters-header h3 {
            font-size: 1rem;
            font-weight: 600;
            color: #1f2937;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .close-filters {
            display: none;
            background: none;
            border: none;
            font-size: 1.5rem;
            color: #9ca3af;
            cursor: pointer;
            padding: 0;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .filters-content {
            padding: 1.25rem;
            max-height: calc(100vh - 200px);
            overflow-y: auto;
          }

          .filter-group {
            margin-bottom: 1.25rem;
          }

          .filter-group label {
            display: block;
            font-size: 0.75rem;
            font-weight: 600;
            color: #374151;
            margin-bottom: 0.5rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .filter-select, .filter-input {
            width: 100%;
            padding: 0.625rem 0.875rem;
            border: 1px solid #e5e7eb;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            color: #374151;
            background: white;
            transition: border-color 0.2s;
          }

          .filter-select:focus, .filter-input:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .filter-buttons {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            margin-top: 1.5rem;
          }

          .filter-btn {
            padding: 0.75rem;
            border: none;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .filter-btn.primary {
            background: #3b82f6;
            color: white;
          }

          .filter-btn.primary:hover {
            background: #2563eb;
            transform: translateY(-1px);
          }

          .filter-btn.secondary {
            background: #f3f4f6;
            color: #374151;
            border: 1px solid #e5e7eb;
          }

          .filter-btn.secondary:hover {
            background: #e5e7eb;
          }

          /* Questions Main */
          .questions-main {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
          }

         /* Toolbar */
.questions-toolbar {
  background: white;
  border-radius: 1rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 1rem;
}

.filter-toggle {
  display: none;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.filter-toggle:hover {
  background: #e5e7eb;
}

.toolbar-controls {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.search-container {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  flex: 1;
  min-width: 200px;
  max-width: 400px;
}

.search-icon {
  padding: 0.75rem;
  color: #9ca3af;
}

.search-input {
  flex: 1;
  padding: 0.75rem;
  border: none;
  font-size: 0.875rem;
  color: #374151;
  min-width: 150px;
}

.search-input:focus {
  outline: none;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-button {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.875rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

          .action-button.primary {
            background: #3b82f6;
            color: white;
          }

          .action-button.primary:hover {
            background: #2563eb;
            transform: translateY(-1px);
          }

          .action-button.success {
            background: #10b981;
            color: white;
          }

          .action-button.success:hover {
            background: #059669;
          }

          .action-button.warning {
            background: #f59e0b;
            color: white;
          }

          .action-button.warning:hover {
            background: #d97706;
          }

          .action-button.danger {
            background: #ef4444;
            color: white;
          }

          .action-button.danger:hover {
            background: #dc2626;
          }

          .action-button.practice {
            background: #8b5cf6;
            color: white;
          }

          .action-button.practice:hover {
            background: #7c3aed;
          }

          .action-button.select-all {
            background: #f3f4f6;
            color: #374151;
            border: 1px solid #e5e7eb;
          }

          .action-button.select-all:hover {
            background: #e5e7eb;
          }

          /* Questions List */
          .questions-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          /* Question Card */
          .question-card {
            background: white;
            border-radius: 1rem;
            border: 1px solid #e2e8f0;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            padding: 1.5rem;
            transition: all 0.3s;
          }

          .question-card:hover {
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
            border-color: #cbd5e0;
          }

          .question-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
          }

          .question-info {
            display: flex;
            align-items: center;
            gap: 1rem;
          }

          .question-number {
            font-size: 1.125rem;
            font-weight: 700;
            color: #3b82f6;
            background: #eff6ff;
            padding: 0.25rem 0.75rem;
            border-radius: 0.5rem;
          }

          .question-id {
            font-size: 0.875rem;
            color: #6b7280;
            font-weight: 500;
          }

          .question-actions {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .action-icon {
            width: 2rem;
            height: 2rem;
            border-radius: 0.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border: none;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-icon.edit {
            background: #d1fae5;
            color: #065f46;
          }

          .action-icon.edit:hover {
            background: #a7f3d0;
          }

          .action-icon.stats {
            background: #e0f2fe;
            color: #0369a1;
          }

          .action-icon.stats:hover {
            background: #bae6fd;
          }

          .question-checkbox {
            width: 1.25rem;
            height: 1.25rem;
            cursor: pointer;
          }

          /* Question Tags */
          .question-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
         
          }

          .tag {
            padding: 0.25rem 0.75rem;
            border-radius: 999px;
            font-size: 0.75rem;
            font-weight: 600;
          }

          .tag.level {
            background: #fef3c7;
            color: #92400e;
          }

          .tag.exam-type {
            background: #e0f2fe;
            color: #0369a1;
          }

          .tag.practice {
            background: #f3e8ff;
            color: #7c3aed;
          }

          /* Question Content */
          .question-content {
            font-size: 0.9375rem;
            line-height: 1.6;
            color: #1f2937;
            margin-bottom: 1.5rem;
          }

          /* Options Section */
          .options-section {
            margin-bottom: 1.5rem;
          }

          .options-header {
            font-size: 0.875rem;
            font-weight: 600;
            color: #374151;
            margin-bottom: 0.75rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .options-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 0.75rem;
          }

          .option-item {
            display: flex;
            gap: 0.75rem;
            padding: 0.875rem;
            background: #f9fafb;
            border-radius: 0.5rem;
            border: 1px solid #e5e7eb;
          }

          .option-letter {
            width: 1.5rem;
            height: 1.5rem;
            background: #e5e7eb;
            color: #374151;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 0.75rem;
            flex-shrink: 0;
          }

          .option-text {
            font-size: 0.875rem;
            line-height: 1.5;
            color: #1f2937;
            flex: 1;
          }

          /* Question Footer */
          .question-footer {
            border-top: 1px solid #f3f4f6;
            padding-top: 1rem;
            display: flex;
            justify-content: flex-end;
          }

          .toggle-answer {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .toggle-answer:hover {
            background: #2563eb;
            transform: translateY(-1px);
          }

          /* Answer Section */
          .answer-section {
            margin-top: 1.5rem;
            padding-top: 1.5rem;
            border-top: 1px dashed #e5e7eb;
          }

          .answer-divider {
            height: 1px;
            background: linear-gradient(90deg, transparent, #e5e7eb, transparent);
            margin-bottom: 1.5rem;
          }

          .answer-content {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .answer-field {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }

          .answer-field strong {
            font-size: 0.875rem;
            color: #374151;
          }

          .answer-text {
            font-size: 0.875rem;
            line-height: 1.6;
            color: #4b5563;
            padding: 0.75rem;
            background: #f9fafb;
            border-radius: 0.5rem;
            border: 1px solid #f3f4f6;
          }

          .option-id {
            font-weight: 600;
            color: #3b82f6;
          }

          /* Load More */
          .load-more {
            text-align: center;
            margin: 2rem 0;
          }

          .load-more-button {
            padding: 0.875rem 2rem;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 0.75rem;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.3);
          }

          .load-more-button:hover {
            background: #2563eb;
            transform: translateY(-2px);
            box-shadow: 0 8px 12px -1px rgba(59, 130, 246, 0.4);
          }

          .load-more-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none;
          }

          .loading-more {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.75rem;
            padding: 2rem;
            color: #6b7280;
            font-size: 0.875rem;
          }

          /* Loading States */
          .loading-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            text-align: center;
          }

          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #e0f2fe;
            border-top: 3px solid #0369a1;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 1rem;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          /* Empty States */
          .empty-state {
            text-align: center;
            padding: 4rem 2rem;
            color: #6b7280;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
            background: white;
            border-radius: 1rem;
            border: 1px solid #e2e8f0;
          }

          .empty-state h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: #374151;
            margin: 0;
          }

          .empty-state p {
            font-size: 0.875rem;
            margin: 0;
          }

          /* Statistics Modal */
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
            backdrop-filter: blur(3px);
          }

          .modal-container {
            background: white;
            border-radius: 1rem;
            width: 90%;
            max-width: 700px;
            max-height: 80vh;
            overflow: hidden;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
          }

          .modal-header {
            padding: 1.5rem;
            border-bottom: 1px solid #f3f4f6;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .modal-header h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: #1f2937;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .modal-close {
            background: none;
            border: none;
            font-size: 1.5rem;
            color: #9ca3af;
            cursor: pointer;
            padding: 0;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: background-color 0.2s;
          }

          .modal-close:hover {
            background: #f3f4f6;
          }

          .modal-tabs {
            display: flex;
            padding: 0.5rem;
            background: #f9fafb;
            border-bottom: 1px solid #f3f4f6;
          }

          .modal-tab {
            flex: 1;
            padding: 0.75rem;
            border: none;
            background: transparent;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            font-weight: 600;
            color: #6b7280;
            cursor: pointer;
            transition: all 0.2s;
          }

          .modal-tab.active {
            background: white;
            color: #3b82f6;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .modal-content {
            max-height: 400px;
            overflow-y: auto;
            padding: 1.5rem;
          }

          .loading-modal {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 3rem;
            text-align: center;
          }

          .empty-modal {
            text-align: center;
            padding: 3rem;
            color: #6b7280;
          }

          .students-list {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }

          .student-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem;
            background: #f9fafb;
            border-radius: 0.75rem;
            border: 1px solid #f3f4f6;
          }

          .student-info {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }

          .student-name {
            font-weight: 600;
            color: #1f2937;
          }

          .student-roll {
            font-size: 0.75rem;
            color: #6b7280;
          }

          .view-student-button {
            padding: 0.5rem 1rem;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 0.5rem;
            font-size: 0.75rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .view-student-button:hover {
            background: #2563eb;
            transform: translateY(-1px);
          }

          /* Responsive Design */
          @media (max-width: 1024px) {
            .questions-content-grid {
              grid-template-columns: 1fr;
            }
            
            .filters-sidebar {
              position: fixed;
              top: 0;
              left: -280px;
              width: 280px;
              height: 100vh;
              background: white;
              z-index: 1000;
              transition: left 0.3s;
              box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
            }
            
            .filters-sidebar.show {
              left: 0;
            }
            
            .filter-toggle {
              display: flex;
            }
            
            .close-filters {
              display: flex;
            }
              .toolbar-controls {
    gap: 0.75rem;
  }
  
  .action-button {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
  }
          }

          @media (max-width: 768px) {
            .questions-container {
              padding: 1rem;
            }
            
          .questions-toolbar {
    flex-direction: column;
    align-items: stretch;
    padding: 0.875rem;
  }
  
            .toolbar-left, .toolbar-right {
              width: 100%;
            }
            
            .toolbar-right {
              justify-content: center;
            }
            
            .search-container {
              max-width: none;
            }
            
            .options-grid {
              grid-template-columns: 1fr;
            }

             .filter-toggle {
    align-self: flex-start;
  }
  
  .toolbar-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
  
  .search-container {
    max-width: none;
    order: 1;
  }
  
  .toolbar-actions {
    order: 2;
    justify-content: center;
    gap: 0.5rem;
  }
  
  .action-button {
    flex: 1;
    justify-content: center;
    min-width: 80px;
  }
          }

          @media (max-width: 480px) {
            .action-button {
              padding: 0.5rem 0.75rem;
              font-size: 0.75rem;
            }
            
            .question-header {
              flex-direction: column;
              align-items: flex-start;
              gap: 1rem;
            }
            
            .question-actions {
              width: 100%;
              justify-content: flex-end;
            }
            
            .modal-container {
              width: 95%;
              margin: 1rem;
            }
          }
        `}
      </style>
    </div>
  );
};

export default QuestionDetails;